import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { request } from '../api/client';
import usePaymentResultRefresh from '../hooks/usePaymentResultRefresh';
import { isUnknownPayment, unknownPaymentMessage } from '../utils/paymentResult';

function Result({ target, onClose }) {
  const [row, setRow] = useState({ id: target.paymentTransactionId, status: 'UNKNOWN' });
  const [error, setError] = useState('');
  const controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), []);
  const refresh = async () => {
    if (target.paymentTransactionId == null) return;
    try {
      controller.current = new AbortController();
      const deadline = Date.now() + 10000;
      let found;
      let page = 1;
      let more = true;
      while (!found && more && Date.now() < deadline && !document.hidden) {
        const data = await request({ url: `/api/${target.domain}/payment/manual`, params: { page, limit: 100 }, timeout: Math.max(1, deadline - Date.now()), signal: controller.current.signal });
        const rows = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
        found = rows.find(item => String(item.paymentTransactionId ?? item.payment_transaction_id ?? item.id) === String(target.paymentTransactionId));
        more = rows.length > 0 && (data?.hasNext ?? (page * Number(data?.limit || 100) < Number(data?.total || 0)));
        page += 1;
      }
      if (found) setRow(found);
      setError(found ? '' : '현재 목록에서 거래를 찾지 못했습니다. 결제내역에서 확인하거나 관리자에게 문의해 주세요.');
    } catch (_) { setError('결과를 조회하지 못했습니다. 잠시 후 다시 확인해 주세요.'); }
  };
  const polling = usePaymentResultRefresh(target.paymentTransactionId == null ? [] : [row], refresh, undefined, true);
  const failed = !isUnknownPayment(row) && String(row.transaction_status ?? row.transactionStatus ?? row.status).toUpperCase() === 'FAILED';
  const confirmed = !isUnknownPayment(row) && ['COMPLETED', 'CANCELED', 'PAID'].includes(String(row.transaction_status ?? row.transactionStatus ?? row.status).toUpperCase());
  useEffect(() => { if (confirmed) window.dispatchEvent(new Event('payment-result-refresh')); }, [confirmed]);
  return <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/45 p-5" role="dialog" aria-modal="true" aria-label="결제 결과 확인">
    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
      <h2 className="text-lg font-bold text-amber-700">{failed ? '결과 확인 완료' : confirmed ? '결과 확인 완료' : target.cancel ? '취소 결과 확인 필요' : '결과 확인 필요'}</h2>
      <p className="mt-4 text-sm leading-6 text-slate-700">{failed ? target.cancel ? '확인 결과 취소되지 않았습니다. 관리자에게 문의해 주세요.' : '확인 결과 결제가 승인되지 않았습니다. 관리자에게 문의해 주세요.' : confirmed ? target.cancel ? '확인 결과 취소가 완료되었습니다.' : '확인 결과 결제가 승인되었습니다.' : unknownPaymentMessage(target.cancel)}</p>
      {!confirmed && !failed && (polling.expired || target.paymentTransactionId == null) && <p className="mt-3 text-sm">결제내역에서 수동으로 확인하거나 관리자에게 문의해 주세요.</p>}
      {error && <p role="status" className="mt-3 text-sm text-amber-700">{error}</p>}
      <div className="mt-5 flex gap-2"><button onClick={onClose} className="flex-1 rounded-lg border p-3">닫기</button>
        {!confirmed && !failed && target.paymentTransactionId != null && <button disabled={polling.checking} onClick={polling.check} className="flex-1 rounded-lg bg-blue-600 p-3 text-white disabled:opacity-50">{polling.checking ? '확인 중…' : '결과 다시 확인'}</button>}
      </div>
    </div>
  </div>;
}

export default function UnknownPaymentResult() {
  const [target, setTarget] = useState(null);
  const { pathname } = useLocation();
  useEffect(() => { setTarget(null); }, [pathname]);
  useEffect(() => { window.__paymentResultDialogOpen = Boolean(target); return () => { window.__paymentResultDialogOpen = false; }; }, [target]);
  useEffect(() => {
    const listener = event => setTarget({ ...event.detail, key: Date.now() });
    window.addEventListener('payment-result-unknown', listener);
    return () => window.removeEventListener('payment-result-unknown', listener);
  }, []);
  return target ? <Result key={target.key} target={target} onClose={() => setTarget(null)} /> : null;
}
